import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { mediaSettingsActions } from 'devices/state';
import { type BlurLevel, type MediaSettingsState, MediaSettingsTab } from 'devices/types';
import { natalieActions } from 'pages/Natalie/state/natalie.actions';
import { agoraActions } from 'store/features/agora/agora.actions';

import { userActions } from './actions';
import { UserState } from './types';

const getBlurLevel = (): BlurLevel => {
  try {
    const blurLevelValue = globalThis.localStorage?.getItem?.('blurLevel');
    return Number(blurLevelValue ?? 0) as BlurLevel;
  } catch {
    return 0 as BlurLevel;
  }
};

export const initialUserState: UserState = {
  mediaDevicesLoaded: false,
  mediaDevices: [],
  mediaSettings: {
    audioInputDeviceId: '',
    audioOutputDeviceId: '',
    videoDeviceId: '',
    blurLevel: getBlurLevel(),
  },
  userSettings: {
    isAudioEnabled: true,
    isVideoEnabled: true,
  },
  isMediaSettingsOpen: false,
  initialMediaSettingsTab: MediaSettingsTab.Video,
  microphoneIssueDetected: false,
  microphoneIssueAcknowledged: false,
  isOnline: true,
  showMemberSetupDialog: false,
  microphonePermissionState: 'unknown',
  cameraPermissionState: 'unknown',
};

export const userSlice = createSlice({
  name: 'user',
  initialState: initialUserState,
  reducers: {
    setAudioEnabled: (state, action: PayloadAction<boolean>) => {
      state.userSettings.isAudioEnabled = action.payload;
    },
    setVideoEnabled: (state, action: PayloadAction<boolean>) => {
      state.userSettings.isVideoEnabled = action.payload;
    },
    setMediaSettings: (state, action: PayloadAction<Partial<MediaSettingsState>>) => {
      state.mediaSettings = {
        ...state.mediaSettings,
        ...action.payload,
      };
    },
    setMediaSettingsIsOpen: (state, { payload }: PayloadAction<{ open: boolean; initialTab?: MediaSettingsTab }>) => {
      state.isMediaSettingsOpen = payload.open;
      state.initialMediaSettingsTab = payload.initialTab ?? MediaSettingsTab.Video;
    },
    setVideoDeviceId: (state, action: PayloadAction<string>) => {
      state.mediaSettings.videoDeviceId = action.payload;
    },
    setAudioInputDeviceId: (state, action: PayloadAction<string>) => {
      state.mediaSettings.audioInputDeviceId = action.payload;
    },
    setMicrophoneIssueDetected: (state, action: PayloadAction<boolean>) => {
      state.microphoneIssueDetected = action.payload;
    },
    setMicrophoneIssueAcknowledged: (state, action: PayloadAction<boolean>) => {
      state.microphoneIssueAcknowledged = action.payload;
    },
    setIsOnline: (state, action: PayloadAction<boolean>) => {
      state.isOnline = action.payload;
    },
    setAudioDisabled: (state) => {
      state.userSettings.isAudioEnabled = false;
    },
    setShowMemberSetupDialog: (state, action) => {
      state.showMemberSetupDialog = action.payload;
    },
    setMicrophonePermissionState: (state, action: PayloadAction<'granted' | 'denied' | 'system_denied' | 'prompt'>) => {
      state.microphonePermissionState = action.payload;
    },
    setCameraPermissionState: (state, action: PayloadAction<'granted' | 'denied' | 'system_denied' | 'prompt'>) => {
      state.cameraPermissionState = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(userActions.navigator.mediaDevices.devicesFound, (state, action) => {
        state.mediaDevices = action.payload;
      })
      .addCase(userActions.navigator.mediaDevices.devicesChanged.fulfilled, (state) => {
        state.mediaDevicesLoaded = true;
      })
      .addCase(userActions.mediaSettings.settingsUpdated, (state, action) => {
        const { audioInputDeviceId, audioOutputDeviceId, blurLevel, isAudioEnabled, isVideoEnabled, videoDeviceId } =
          action.payload;

        state.mediaSettings = {
          audioInputDeviceId,
          audioOutputDeviceId,
          blurLevel,
          videoDeviceId,
        };

        state.userSettings = {
          isAudioEnabled,
          isVideoEnabled,
        };
      })
      .addCase(agoraActions.audioTrack.toggling.failed, (state) => {
        state.userSettings.isAudioEnabled = !state.userSettings.isAudioEnabled;
      })
      .addCase(agoraActions.videoTrack.toggling.failed, (state) => {
        state.userSettings.isVideoEnabled = !state.userSettings.isVideoEnabled;
      })
      .addCase(mediaSettingsActions.toggleMedia.openAudioSettingsClicked, (state) => {
        state.isMediaSettingsOpen = true;
        state.initialMediaSettingsTab = MediaSettingsTab.Audio;
      })
      .addCase(mediaSettingsActions.toggleMedia.openVideoSettingsClicked, (state) => {
        state.isMediaSettingsOpen = true;
        state.initialMediaSettingsTab = MediaSettingsTab.Video;
      })
      .addCase(
        natalieActions.joinPage.joinButton.clicked,
        (state, { payload: { isMicrophoneEnabled, microphoneId, speakerId } }) => {
          state.mediaSettings.audioInputDeviceId = microphoneId;
          state.mediaSettings.audioOutputDeviceId = speakerId;
          state.userSettings.isAudioEnabled = isMicrophoneEnabled;
        }
      );
  },
});
